// project-mentions — extract canonical condo project names from tool
// outputs so the chat UI can render clickable chips next to assistant
// turns. The whitelist mirrors the project-aware tools registered in
// backend/internal/tools (find_property_location, lookup_project_profile,
// lookup_unit_floorplan, project_unit_mix, lookup_private_transactions,
// new_launch_pipeline). Any tool not on the list returns []. Unknown
// shapes never throw — bad JSON is silently ignored.
//
// Names are deduped case-insensitively but the original casing is
// preserved on first occurrence so the chip label matches what the
// agent already showed in prose.

const MAX_PER_TOOL = 8;

type Output = unknown;

function asObject(v: Output): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function pushUnique(out: string[], seen: Set<string>, name: unknown): void {
  if (typeof name !== "string") return;
  const trimmed = name.trim();
  if (!trimmed) return;
  if (looksLikeGenericLabel(trimmed)) return;
  const key = trimmed.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push(trimmed);
}

// looksLikeGenericLabel filters out URA pipeline placeholders that occupy
// the `project` field but aren't real project names — e.g. "Service
// apartments", "Service apartments/Hotel/retail development", "Mixed
// development". These are URA's category labels for unnamed strata
// developments; rendering them as clickable project chips leads to a
// dead-end project lookup ("no details") because no such project exists.
// Real project names always start with a capitalised proper noun (a
// digit-prefixed address like "51 Anson..." also passes).
function looksLikeGenericLabel(name: string): boolean {
  // First whitespace-split token; if it's a known property-typology common
  // noun (lowercase form), treat the whole string as a category label.
  const first = name.split(/[\s/]+/, 1)[0]?.toLowerCase() ?? "";
  const generic = new Set([
    "service",
    "mixed",
    "mixed-use",
    "strata",
    "apartment",
    "apartments",
    "condominium",
    "condo",
    "flat",
    "flats",
    "hotel",
    "retail",
    "townhouse",
    "townhouses",
    "detached",
    "semi-detached",
    "terrace",
    "cluster",
    "shophouse",
    "shophouses",
    "development",
    "redevelopment",
  ]);
  return generic.has(first);
}

export function extractProjects(toolName: string, rawOutput: string): string[] {
  if (!rawOutput || !toolName) return [];
  let parsed: Output;
  try {
    parsed = JSON.parse(rawOutput);
  } catch {
    return [];
  }
  const obj = asObject(parsed);
  if (!obj) return [];

  // A soft-failure shape ({error_code, error_message, retryable}) is the
  // tool's "no data" signal — don't surface chips from those.
  if ("error_code" in obj) return [];

  const out: string[] = [];
  const seen = new Set<string>();

  switch (toolName) {
    case "find_property_location": {
      for (const c of asArray(obj.candidates)) {
        const co = asObject(c);
        if (co) pushUnique(out, seen, co.name);
        if (out.length >= MAX_PER_TOOL) break;
      }
      break;
    }
    case "lookup_project_profile":
    case "lookup_unit_floorplan":
    case "project_unit_mix": {
      pushUnique(out, seen, obj.name);
      break;
    }
    case "lookup_private_transactions": {
      for (const t of asArray(obj.transactions)) {
        const to = asObject(t);
        if (to) pushUnique(out, seen, to.project);
        if (out.length >= MAX_PER_TOOL) break;
      }
      break;
    }
    case "new_launch_pipeline": {
      for (const p of asArray(obj.projects)) {
        const po = asObject(p);
        if (po) pushUnique(out, seen, po.project);
        if (out.length >= MAX_PER_TOOL) break;
      }
      break;
    }
    default:
      return [];
  }
  return out;
}

// dedupe merges several project lists preserving first-seen casing.
export function dedupeProjects(...lists: ReadonlyArray<readonly string[]>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const name of list) pushUnique(out, seen, name);
  }
  return out;
}
